import React, { useState } from "react";
import WorkflowPauseStage from "./WorkflowPauseStage";
import WorkflowCaseActionBanner, { WorkflowCaseActionBannerKind } from "./WorkflowCaseActionBanner";
import { WorkflowPauseConfig } from "./workflowBuilderData";
import { WorkflowLiveCase } from "./workflowLiveCase";
import { toStampTime } from "./workflowRunState";
import useWorkflowCaseAction from "../../hooks/useWorkflowCaseAction";
import { useToaster } from "../../hooks/useToaster";
import { resumeWorkflowRun } from "../../services/workflowService";
import { SEVERITIES } from "../../data/constants";

interface WorkflowPauseLiveProps {
  config: WorkflowPauseConfig;
  owner: string;
  pausedUntil: string | null;
  liveCase: WorkflowLiveCase;
}

/** A Pause stage on a real case: its own resume time, and Resume now via instance/resume — which
 *  the engine allows only for the pause owner (a 403 says so). */
const WorkflowPauseLive: React.FC<WorkflowPauseLiveProps> = ({ config, owner, pausedUntil, liveCase }) => {
  const showToaster = useToaster();
  const { busy, run } = useWorkflowCaseAction();
  const [banner, setBanner] = useState<WorkflowCaseActionBannerKind | null>(null);
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null);

  const reload = async () => {
    setBanner((await liveCase.onChanged()) ? null : "reload-failed");
  };

  const handleResume = async () => {
    const outcome = await run(resumeWorkflowRun, {
      instanceId: liveCase.instanceId,
      rowVersionTag: liveCase.rowVersionTag,
    });
    if (!outcome) return;
    if (outcome.kind !== "done") {
      setInvalidMessage(outcome.kind === "invalid" ? outcome.message : null);
      setBanner(outcome.kind);
      return;
    }
    showToaster("Resumed", SEVERITIES.SUCCESS);
    await reload();
  };

  return (
    <WorkflowPauseStage
      config={config}
      owner={owner}
      pausedUntil={pausedUntil ? toStampTime(pausedUntil) : undefined}
      busy={busy}
      onResume={() => void handleResume()}
      banner={
        <WorkflowCaseActionBanner
          kind={banner}
          forbiddenMessage="Only the pause owner can resume this case early."
          invalidMessage={invalidMessage}
          onReload={() => void reload()}
        />
      }
    />
  );
};

export default WorkflowPauseLive;
